// Languages — console-standard rebuild. Location-language coverage + the admin's
// own dashboard language. Body is theme-aware; default export wraps in ConsoleShell
// unless rendered embedded inside a (themed) hub.
import React, { useState, useEffect } from 'react';
import { ConsoleShell, KPI, Panel, SectionLabel, Empty, useConsoleTheme, useIsMobile } from '../components/Console';
import { supabase } from '../supabaseClient';

const fmt = n => (n == null ? '…' : Number(n).toLocaleString());
const cleanLangName = raw => { const s = (raw || '').trim().replace(/\s*\(.*?\)\s*/g, '').trim(); return s.charAt(0).toUpperCase() + s.slice(1); };
const baseLang = raw => (raw || '').toLowerCase().trim().replace(/\s*\(.*?\)\s*/g, '').trim();

const LANG_TO_FLAG = {
  english:'🇬🇧', french:'🇫🇷', spanish:'🇪🇸', arabic:'🇸🇦', german:'🇩🇪', japanese:'🇯🇵', korean:'🇰🇷', thai:'🇹🇭',
  chinese:'🇨🇳', portuguese:'🇧🇷', italian:'🇮🇹', dutch:'🇳🇱', malay:'🇲🇾', indonesian:'🇮🇩', vietnamese:'🇻🇳', filipino:'🇵🇭',
  hindi:'🇮🇳', tamil:'🇸🇬', polish:'🇵🇱', norwegian:'🇳🇴', swedish:'🇸🇪', danish:'🇩🇰', finnish:'🇫🇮', czech:'🇨🇿',
  romanian:'🇷🇴', hungarian:'🇭🇺', greek:'🇬🇷', turkish:'🇹🇷', ukrainian:'🇺🇦', mandarin:'🇨🇳', taiwanese:'🇹🇼',
  cantonese:'🇭🇰', burmese:'🇲🇲',
};
const getFlag = lang => LANG_TO_FLAG[baseLang(lang)] || null;

const DASHBOARD_LANGS = [
  { code:'en', label:'English', flag:'🇬🇧' }, { code:'fr', label:'French', flag:'🇫🇷' }, { code:'es', label:'Spanish', flag:'🇪🇸' },
  { code:'ar', label:'Arabic', flag:'🇸🇦' }, { code:'de', label:'German', flag:'🇩🇪' }, { code:'ja', label:'Japanese', flag:'🇯🇵' },
  { code:'ko', label:'Korean', flag:'🇰🇷' }, { code:'th', label:'Thai', flag:'🇹🇭' }, { code:'zh', label:'Chinese', flag:'🇨🇳' },
  { code:'pt', label:'Portuguese', flag:'🇧🇷' }, { code:'ms', label:'Malay', flag:'🇲🇾' }, { code:'vi', label:'Vietnamese', flag:'🇻🇳' },
  { code:'id', label:'Indonesian', flag:'🇮🇩' },
];

function LocationLanguages() {
  const { t } = useConsoleTheme();
  const [langs, setLangs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [allLocs, setAllLocs] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { count } = await supabase.from('Locations').select('id', { count:'exact', head:true });
        if (!cancelled) setAllLocs(count || 0);
        let rows = [];
        const { data: rpcData, error: rpcErr } = await supabase.rpc('get_language_stats');
        if (!rpcErr && rpcData && rpcData.length > 0) rows = rpcData;
        else {
          const { data: direct } = await supabase.from('Locations').select('languages')
            .not('languages','is',null).neq('languages','').limit(5000);
          if (direct && direct.length > 0) {
            const counts = {};
            direct.forEach(row => (row.languages || '').split(/[,;|]+/).map(l => l.trim().toLowerCase()).filter(Boolean)
              .forEach(lang => { counts[lang] = (counts[lang] || 0) + 1; }));
            rows = Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([language, location_count]) => ({ language, location_count }));
          }
        }
        if (!cancelled) { setLangs(rows); setTotal(rows.reduce((s, r) => s + Number(r.location_count), 0)); }
      } catch (_e) {}
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const maxCount = langs[0]?.location_count || 1;
  const coveragePct = allLocs > 0 ? parseFloat(((total / allLocs) * 100).toFixed(1)) : null;

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:10 }}>
        <KPI label="Languages detected" value={loading ? '…' : fmt(langs.length)} sub="unique across directory" accent="#314F5C" />
        <KPI label="Top language" value={loading ? '…' : cleanLangName(langs[0]?.language || '—')} sub={loading ? '' : `${fmt(langs[0]?.location_count)} locations`} accent={t.accent} />
        <KPI label="Locations tagged" value={loading ? '…' : fmt(total)} sub={coveragePct != null ? `${coveragePct}% of directory` : 'of all locations'} subColor={coveragePct != null && coveragePct < 10 ? t.warn : t.mut} accent="#819D84" />
      </div>

      {!loading && coveragePct != null && coveragePct < 10 && (
        <div style={{ marginTop:12 }}>
          <Empty>Only {coveragePct}% of your {fmt(allLocs)} locations carry language data. Tagging more records helps travellers find care in their language.</Empty>
        </div>
      )}

      <SectionLabel right={!loading && langs.length > 0 ? `${fmt(total)} tagged locations` : ''}>Language breakdown</SectionLabel>
      <Panel>
        {loading ? <Empty>Loading language coverage…</Empty> : langs.length === 0 ? <Empty>No language data found — add language tags to location records to see coverage here.</Empty>
          : langs.map(({ language, location_count }, idx) => {
              const pct = Math.round((Number(location_count) / Number(maxCount)) * 100);
              const sharePct = total ? (Number(location_count) / total * 100).toFixed(1) : 0;
              const flag = getFlag(language); const name = cleanLangName(language);
              const op = Math.max(0.35, 1 - idx * 0.07);
              return (
                <div key={language} style={{ display:'flex', alignItems:'center', gap:11, padding:'4px 5px', borderRadius:7 }}>
                  <span style={{ fontSize:'0.78rem', color:t.mut, fontWeight:700, width:18, textAlign:'right', flexShrink:0, fontVariantNumeric:'tabular-nums' }}>{idx + 1}</span>
                  <div style={{ width:28, height:28, borderRadius:7, background:t.gbar, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {flag ? <span style={{ fontSize:'1rem', lineHeight:1 }}>{flag}</span> : <span style={{ fontSize:'0.7rem', fontWeight:700, color:t.accent }}>{name.slice(0, 2).toUpperCase()}</span>}
                  </div>
                  <span style={{ fontSize:'0.98rem', color:t.tx, fontWeight:500, width:120, flexShrink:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{name}</span>
                  <div style={{ flex:1, height:14, background:t.gbar, borderRadius:5, overflow:'hidden' }}>
                    <div style={{ width:`${pct}%`, height:'100%', background:t.accent, opacity:op, borderRadius:5, transition:'width .5s ease' }} />
                  </div>
                  <span style={{ fontSize:'0.92rem', color:t.mut, width:104, textAlign:'right', flexShrink:0, fontVariantNumeric:'tabular-nums' }}>
                    {fmt(location_count)} <span style={{ color:t.mut, opacity:0.7, fontSize:'0.82rem' }}>({sharePct}%)</span>
                  </span>
                </div>
              );
            })}
      </Panel>
    </div>
  );
}

function MyLanguage() {
  const { t } = useConsoleTheme();
  const [current, setCurrent] = useState('en');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const { data: { user } } = await supabase.auth.getUser(); const meta = user?.user_metadata?.dashboard_language; if (meta && !cancelled) setCurrent(meta); } catch (_e) {}
    })();
    return () => { cancelled = true; };
  }, []);

  async function save() {
    setSaving(true);
    try { await supabase.auth.updateUser({ data: { dashboard_language: current } }); setSaved(true); setTimeout(() => setSaved(false), 2500); } catch (_e) {}
    setSaving(false);
  }

  return (
    <div>
      <SectionLabel>Your dashboard language</SectionLabel>
      <Panel>
        <div style={{ fontSize:'0.98rem', color:t.mut, marginBottom:14, lineHeight:1.6 }}>
          Saved to your admin profile and only affects your view. Other team members keep their own setting.
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:9 }}>
          {DASHBOARD_LANGS.map(({ code, label, flag }) => {
            const active = current === code;
            return (
              <div key={code} onClick={() => setCurrent(code)}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 13px', borderRadius:9, cursor:'pointer', transition:'all .12s',
                  border:`1.5px solid ${active ? t.accent : t.bd}`, background: active ? t.rowbg : t.kbg,
                  boxShadow: active ? `0 0 0 3px ${t.accent}22` : 'none' }}>
                <span style={{ fontSize:'1.25rem' }}>{flag}</span>
                <span style={{ fontSize:'0.98rem', fontWeight: active ? 600 : 400, color: active ? t.tx : t.mut }}>{label}</span>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop:16, display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={save} disabled={saving}
            style={{ fontSize:'0.95rem', padding:'8px 16px', borderRadius:8, cursor: saving ? 'default' : 'pointer', border:'none', background:t.accent, color: t.bg === '#f2f1ef' ? '#fff' : '#06222e', fontWeight:600, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : 'Save preference'}
          </button>
          {saved && <span style={{ fontSize:'0.92rem', color:t.up, fontWeight:600 }}>Saved ✓</span>}
        </div>
      </Panel>
    </div>
  );
}

function Body() {
  const { t } = useConsoleTheme();
  const [tab, setTab] = useState(0);
  const tabs = ['Location languages', 'My language'];
  return (
    <div>
      <div style={{ display:'inline-flex', gap:4, padding:4, background:t.period, border:`1px solid ${t.bd}`, borderRadius:10, marginBottom:14 }}>
        {tabs.map((label, i) => (
          <button key={i} onClick={() => setTab(i)}
            style={{ fontSize:'0.95rem', padding:'6px 14px', borderRadius:7, cursor:'pointer', border:'none', fontWeight: tab === i ? 600 : 500,
              background: tab === i ? t.accent : 'transparent', color: tab === i ? (t.bg === '#f2f1ef' ? '#fff' : '#06222e') : t.mut, fontFamily:'inherit' }}>
            {label}
          </button>
        ))}
      </div>
      {tab === 0 ? <LocationLanguages /> : <MyLanguage />}
    </div>
  );
}

export default function Languages(props) {
  if (props && props.embed) return <Body />;
  return <ConsoleShell title="Languages"><Body /></ConsoleShell>;
}
